import type { ExecutionMatrixRow, SchemeExecutionCell } from "@/types/client";
import {
  BuildTableParams,
  FIXED_COLUMN_LABELS,
  SCHEME_SUB_LABELS,
  formatRowPov,
  formatCellPov,
} from "./executionTableHelpers";

// Hex literals are deliberate: this HTML is pasted into email/Excel, where the app's var(--token) colours cannot resolve.
const STYLE = {
  table: "border-collapse:collapse;font-family:Arial,sans-serif;font-size:13px;",
  title: "font-weight:bold;font-size:15px;padding:8px 6px 4px;border:none;",
  headerFixed:
    "background:#e8e8e8;font-weight:bold;border:1px solid #999;padding:5px 8px;text-align:center;white-space:nowrap;",
  headerSub:
    "background:#f2f2f2;font-size:11px;color:#555;border:1px solid #ccc;padding:3px 6px;text-align:center;",
  // Leaf header alignment must match its column: text left, numbers right.
  alignLeft: "text-align:left;",
  alignRight: "text-align:right;",
  cellText: "border:1px solid #ccc;padding:4px 8px;text-align:left;white-space:nowrap;",
  cellNum: "border:1px solid #ccc;padding:4px 8px;text-align:right;white-space:nowrap;",
  spacer: "border:none;padding:8px;",
};

/**
 * Builds an HTML table string of the execution matrix (buy + sell) for clipboard.
 * Excel reads the text/html clipboard flavor and applies inline styles including borders.
 */
export function buildExecutionTableHTML({
  buyResults,
  sellResults,
  schemes,
  cmpData,
  getDifference,
}: BuildTableParams): string {
  const rows: string[] = [];

  if (buyResults.length > 0) {
    rows.push(...buildSection("BUY ORDERS", buyResults, schemes, cmpData, getDifference, 1));
  }
  if (buyResults.length > 0 && sellResults.length > 0) {
    const totalCols = FIXED_COLUMN_LABELS.length + schemes.length * SCHEME_SUB_LABELS.length;
    const emptyCells = Array(totalCols).fill(`<td style="${STYLE.spacer}"></td>`).join("");
    rows.push(`<tr>${emptyCells}</tr>`);
  }
  if (sellResults.length > 0) {
    rows.push(...buildSection("SELL ORDERS", sellResults, schemes, cmpData, getDifference, 2));
  }

  const table = `<table border="1" cellspacing="0" cellpadding="0" style="${STYLE.table}">${rows.join("")}</table>`;
  return `<html><body>${table}</body></html>`;
}

function buildSection(
  title: string,
  rows: ExecutionMatrixRow[],
  schemes: string[],
  cmpData: Record<number, number | null>,
  getDifference: (companyId: number, limitPrice: number, orderSide: 1 | 2) => { value: number; pct: number } | null,
  side: 1 | 2,
): string[] {
  return [
    buildTitleRow(title, schemes),
    buildHeaderRow1(schemes),
    buildHeaderRow2(schemes),
    ...rows.map((row) => buildDataRow(row, schemes, cmpData, getDifference, side)),
  ];
}

function buildTitleRow(title: string, schemes: string[]): string {
  const totalCols = FIXED_COLUMN_LABELS.length + schemes.length * SCHEME_SUB_LABELS.length;
  const emptyCells = Array(totalCols - 1).fill(`<td style="${STYLE.spacer}"></td>`).join("");
  return `<tr><td style="${STYLE.title}">${title}</td>${emptyCells}</tr>`;
}

function buildHeaderRow1(schemes: string[]): string {
  // First fixed column (symbol) is text; the rest are numeric.
  const fixedCols = FIXED_COLUMN_LABELS.map(
    (h, i) => `<th style="${STYLE.headerFixed}${i === 0 ? STYLE.alignLeft : STYLE.alignRight}">${h}</th>`,
  ).join("");
  const schemeCols = schemes
    .map((s) => `<th colspan="${SCHEME_SUB_LABELS.length}" style="${STYLE.headerFixed}">${s}</th>`)
    .join("");
  return `<tr>${fixedCols}${schemeCols}</tr>`;
}

function buildHeaderRow2(schemes: string[]): string {
  const fixedEmpty = FIXED_COLUMN_LABELS.map(
    (_, i) => `<th style="${STYLE.headerSub}${i === 0 ? STYLE.alignLeft : STYLE.alignRight}"></th>`,
  ).join("");
  const schemeCols = schemes
    .map(() => SCHEME_SUB_LABELS.map((sub) => `<th style="${STYLE.headerSub}${STYLE.alignRight}">${sub}</th>`).join(""))
    .join("");
  return `<tr>${fixedEmpty}${schemeCols}</tr>`;
}

function buildDataRow(
  row: ExecutionMatrixRow,
  schemes: string[],
  cmpData: Record<number, number | null>,
  getDifference: (companyId: number, limitPrice: number, orderSide: 1 | 2) => { value: number; pct: number } | null,
  side: 1 | 2,
): string {
  const cmp = cmpData[row.company.id];
  const slip = getDifference(row.company.id, row.limit_price, side);

  const fixedCells = [
    td(row.company.symbol, STYLE.cellText),
    td(row.limit_price, STYLE.cellNum),
    td(cmp ?? "", STYLE.cellNum),
    td(slip != null ? slip.value.toFixed(2) : "", STYLE.cellNum),
    td(slip != null ? slip.pct.toFixed(2) : "", STYLE.cellNum),
    td(formatRowPov(row), STYLE.cellNum),
  ].join("");

  const schemeCells = schemes
    .map((schemeCode) => {
      const cell = row.schemes[schemeCode] as SchemeExecutionCell | null;
      if (!cell) {
        return `${td("", STYLE.cellNum)}${td("", STYLE.cellNum)}${td("", STYLE.cellNum)}`;
      }
      return `${td(cell.ordered_qty, STYLE.cellNum)}${td(cell.executed_qty, STYLE.cellNum)}${td(formatCellPov(cell), STYLE.cellNum)}`;
    })
    .join("");

  return `<tr>${fixedCells}${schemeCells}</tr>`;
}

function td(value: string | number, style: string): string {
  return `<td style="${style}">${value}</td>`;
}
